import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { InstanceFooter } from '@/components/layout/InstanceFooter';
import {
  UserModal,
  TagModal,
  ClassificationModal,
  StatusModal,
  ProductModal,
  SubProductModal,
  TimeframeModal,
} from '@/components/modals';
import {
  userApi,
  tagApi,
  classificationApi,
  statusApi,
  productApi,
  subProductApi,
  fiscalYearApi,
  organizationApi,
  fieldConfigApi,
  labsApi,
  User,
  Organization,
  FieldConfig,
  LabsFeature,
} from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useAppStore } from '@/stores/appStore';
import { useFieldConfig } from '@/hooks/useFieldConfig';
import { OrganizationTab } from '@/components/settings/OrganizationTab';
import { InitiativeFieldsTab } from '@/components/settings/InitiativeFieldsTab';
import { HierarchyTab } from '@/components/settings/HierarchyTab';
import { ClassificationsTab } from '@/components/settings/ClassificationsTab';
import { StatusTab } from '@/components/settings/StatusTab';
import { TagsTab } from '@/components/settings/TagsTab';
import { UsersTab } from '@/components/settings/UsersTab';
import { TimeframesTab } from '@/components/settings/TimeframesTab';
import { LabsTab } from '@/components/settings/LabsTab';
import { AiProviderTab } from '@/components/settings/AiProviderTab';
import { ApiKeysTab } from '@/components/settings/ApiKeysTab';
import { TeamsTab } from '@/components/settings/TeamsTab';
import { GenerateKeyModal } from '@/components/modals/GenerateKeyModal';
import { apiKeyApi, ApiKey } from '@/services/api';

type SettingsTab = 'organization' | 'fields' | 'hierarchy' | 'classifications' | 'statuses' | 'tags' | 'users' | 'timeframes' | 'labs' | 'ai-provider' | 'api-keys' | 'teams';

export function SettingsPage() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'ADMIN';
  const [activeTab, setActiveTab] = useState<SettingsTab>(isAdmin ? 'organization' : 'hierarchy');
  const [searchParams] = useSearchParams();
  const fc = useFieldConfig();

  // Allow tour to navigate directly to a tab via ?tab= URL param
  useEffect(() => {
    const tab = searchParams.get('tab') as SettingsTab | null;
    const validTabs: SettingsTab[] = ['organization', 'fields', 'hierarchy', 'classifications', 'statuses', 'tags', 'users', 'timeframes', 'labs', 'ai-provider', 'api-keys', 'teams'];
    if (tab && validTabs.includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Data state
  const [products, setProducts] = useState<any[]>([]);
  const [subProducts, setSubProducts] = useState<any[]>([]);
  const [classifications, setClassifications] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [tags, setTags] = useState<any[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [fiscalYears, setFiscalYears] = useState<any[]>([]);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Field config state
  const [fieldConfigs, setFieldConfigs] = useState<FieldConfig[]>([]);
  const [editedFields, setEditedFields] = useState<Record<string, Partial<FieldConfig>>>({});
  const [fieldConfigSaving, setFieldConfigSaving] = useState(false);
  const setStoreFieldConfigs = useAppStore((s) => s.setFieldConfigs);

  // Labs state
  const [labsFeatures, setLabsFeatures] = useState<LabsFeature[]>([]);
  const [labsSaving, setLabsSaving] = useState(false);
  const [labsSaved, setLabsSaved] = useState(false);
  const [labsSaveError, setLabsSaveError] = useState('');
  const setStoreLabsFeatures = useAppStore((s) => s.setLabsFeatures);

  // Hierarchy label state
  const [labelDraft, setLabelDraft] = useState({ l1: 'Product', l2: 'Sub-product', l3: 'Initiative' });
  const [labelSaving, setLabelSaving] = useState(false);
  const [labelSaved, setLabelSaved] = useState(false);
  const [labelSaveError, setLabelSaveError] = useState('');

  // Allowed email domains state
  const [domainInput, setDomainInput] = useState('');
  const [domainSaving, setDomainSaving] = useState(false);
  const [domainSaved, setDomainSaved] = useState(false);
  const [domainError, setDomainError] = useState('');

  // Modal state
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [subProductModalOpen, setSubProductModalOpen] = useState(false);
  const [classificationModalOpen, setClassificationModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [fiscalYearModalOpen, setFiscalYearModalOpen] = useState(false);

  // Selected items for editing
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [selectedSubProduct, setSelectedSubProduct] = useState<any>(null);
  const [selectedClassification, setSelectedClassification] = useState<any>(null);
  const [selectedStatus, setSelectedStatus] = useState<any>(null);
  const [selectedTag, setSelectedTag] = useState<any>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState<any>(null);

  // Resend invite state
  const [resendState, setResendState] = useState<Record<string, 'sending' | 'sent'>>({});

  // API keys state
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [generateKeyModalOpen, setGenerateKeyModalOpen] = useState(false);
  const [isRevoking, setIsRevoking] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [productsData, subProductsData, classificationsData, statusesData, tagsData, fiscalYearsData] =
        await Promise.all([
          productApi.getAll(),
          subProductApi.getAll(),
          classificationApi.getAll(),
          statusApi.getAll(),
          tagApi.getAll(),
          fiscalYearApi.getAll(),
        ]);

      setProducts(productsData);
      setSubProducts(subProductsData);
      setClassifications(classificationsData);
      setStatuses(statusesData);
      setTags(tagsData);
      setFiscalYears(fiscalYearsData);

      try { setUsers(await userApi.getAll()); } catch { /**/ }
      try {
        const configs = await fieldConfigApi.getAll();
        setFieldConfigs(configs);
        setEditedFields({});
      } catch { /**/ }
      try {
        const labs = await labsApi.getAll();
        setLabsFeatures(labs);
        if (labs.length > 0) setStoreLabsFeatures(labs);
      } catch { /**/ }
      if (isAdmin) {
        try { setOrganization(await organizationApi.get()); } catch { /**/ }
        try {
          const keysResult = await apiKeyApi.getAll();
          if (keysResult) setApiKeys(keysResult.keys);
        } catch { /**/ }
      }
    } catch (error) {
      console.error('Failed to load settings data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);
  useEffect(() => {
    if (organization) setDomainInput(organization.allowedEmailDomains.join(', '));
  }, [organization]);

  // Initialise label draft from field configs once loaded
  useEffect(() => {
    if (fieldConfigs.length > 0) {
      const p = fieldConfigs.find((f) => f.fieldKey === 'product');
      const sp = fieldConfigs.find((f) => f.fieldKey === 'subProduct');
      const ini = fieldConfigs.find((f) => f.fieldKey === 'initiative');
      setLabelDraft({
        l1: p?.label || 'Product',
        l2: sp?.label || 'Sub-product',
        l3: ini?.label || 'Initiative',
      });
    }
  }, [fieldConfigs]);

  // --- Handlers ---
  const handleSaveDomains = async () => {
    setDomainError('');
    setDomainSaving(true);
    try {
      const domains = domainInput.split(',').map(d => d.trim().replace(/^@/, '').toLowerCase()).filter(d => d.length > 0);
      const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/;
      for (const domain of domains) {
        if (!domainRegex.test(domain)) { setDomainError(`Invalid domain: ${domain}`); setDomainSaving(false); return; }
      }
      const updatedOrg = await organizationApi.update({ allowedEmailDomains: domains });
      setOrganization(updatedOrg);
      setDomainInput(updatedOrg.allowedEmailDomains.join(', '));
      setDomainSaved(true);
      setTimeout(() => setDomainSaved(false), 2000);
    } catch (error) {
      setDomainError(error instanceof Error ? error.message : 'Failed to save');
    } finally {
      setDomainSaving(false);
    }
  };

  const handleResendInvite = async (userId: string) => {
    setResendState(prev => ({ ...prev, [userId]: 'sending' }));
    try {
      await userApi.resendInvite(userId);
      setResendState(prev => ({ ...prev, [userId]: 'sent' }));
      setTimeout(() => setResendState(prev => { const n = { ...prev }; delete n[userId]; return n; }), 2000);
    } catch {
      setResendState(prev => { const n = { ...prev }; delete n[userId]; return n; });
    }
  };

  const handleRevokeKey = async (id: string) => {
    if (!confirm('Revoke this API key? Any integrations using it will stop working immediately.')) return;
    setIsRevoking(id);
    try {
      await apiKeyApi.revoke(id);
      setApiKeys((prev) => prev.filter((k) => k.id !== id));
    } catch (err) {
      console.error('Failed to revoke key:', err);
    } finally {
      setIsRevoking(null);
    }
  };

  const handleSaveFieldConfigs = async () => {
    setFieldConfigSaving(true);
    try {
      const fields = Object.entries(editedFields).map(([fieldKey, changes]) => ({ fieldKey, ...changes }));
      const updated = await fieldConfigApi.update(fields);
      setFieldConfigs(updated);
      setEditedFields({});
      setStoreFieldConfigs(updated);
    } catch (error) {
      console.error('Failed to save field configs:', error);
    } finally {
      setFieldConfigSaving(false);
    }
  };

  const handleSaveLabels = async () => {
    setLabelSaving(true);
    setLabelSaved(false);
    setLabelSaveError('');
    try {
      const trimmed = {
        l1: labelDraft.l1.trim() || 'Product',
        l2: labelDraft.l2.trim() || 'Sub-product',
        l3: labelDraft.l3.trim() || 'Initiative',
      };
      setLabelDraft(trimmed);
      const updated = await fieldConfigApi.update([
        { fieldKey: 'product', label: trimmed.l1 },
        { fieldKey: 'subProduct', label: trimmed.l2 },
        { fieldKey: 'initiative', label: trimmed.l3 },
      ]);
      setFieldConfigs(updated);
      setStoreFieldConfigs(updated);
      setLabelSaved(true);
      setTimeout(() => setLabelSaved(false), 2000);
    } catch (error) {
      setLabelSaveError(error instanceof Error ? error.message : 'Failed to save labels');
    } finally {
      setLabelSaving(false);
    }
  };

  const handleSaveLabsFeatures = async () => {
    setLabsSaving(true);
    setLabsSaved(false);
    setLabsSaveError('');
    if (labsFeatures.length === 0) {
      setLabsSaveError('No labs features loaded. Please refresh and try again.');
      setLabsSaving(false);
      return;
    }
    try {
      const updated = await labsApi.update(labsFeatures);
      setLabsFeatures(updated);
      setStoreLabsFeatures(updated);
      setLabsSaved(true);
      setTimeout(() => setLabsSaved(false), 2000);
    } catch (error) {
      setLabsSaveError(error instanceof Error ? error.message : 'Failed to save');
    } finally {
      setLabsSaving(false);
    }
  };

  // Cascade both directions: parent ON → all children ON, parent OFF → all children OFF
  const handleToggleLabsFeature = (featureKey: string, isEnabled: boolean) => {
    setLabsFeatures(prev => prev.map(f => {
      if (f.featureKey === featureKey) return { ...f, isEnabled };
      if (f.parentFeatureKey === featureKey) return { ...f, isEnabled };
      return f;
    }));
  };

  const tabClass = (tab: SettingsTab) =>
    `whitespace-nowrap px-2.5 xl:px-4 py-2 text-sm font-medium rounded-md ${
      activeTab === tab
        ? 'bg-background-primary text-text-primary'
        : 'text-text-secondary hover:text-text-primary'
    }`;

  return (
    <div className="h-screen flex flex-col bg-background-primary">
      <Navbar />
      <main className="flex-1 overflow-hidden">
        <div className="h-full flex flex-col">
          <div className="px-3 xl:px-6 py-2 bg-background-secondary border-b border-border-subtle">
            <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
              {isAdmin && <button onClick={() => setActiveTab('organization')} className={tabClass('organization')}>Organization</button>}
              <button data-tour-id="settings-hierarchy-tab" onClick={() => setActiveTab('hierarchy')} className={tabClass('hierarchy')}>Hierarchy</button>
              {isAdmin && <button onClick={() => setActiveTab('fields')} className={tabClass('fields')}>{fc.label('initiative')} Fields</button>}
              <button onClick={() => setActiveTab('statuses')} className={tabClass('statuses')}>Status</button>
              <button onClick={() => setActiveTab('classifications')} className={tabClass('classifications')}>Classifications</button>
              <button onClick={() => setActiveTab('tags')} className={tabClass('tags')}>Tags</button>
              <button onClick={() => setActiveTab('timeframes')} className={tabClass('timeframes')}>Timeframes</button>
              {isAdmin && <button onClick={() => setActiveTab('teams')} className={tabClass('teams')}>Teams</button>}
              <button data-tour-id="settings-users-tab" onClick={() => setActiveTab('users')} className={tabClass('users')}>Users</button>
              {isAdmin && <button onClick={() => setActiveTab('labs')} className={tabClass('labs')}>Labs</button>}
              {isAdmin && labsFeatures.some(f => f.featureKey === 'intelligence' && f.isEnabled) && (
                <button onClick={() => setActiveTab('ai-provider')} className={tabClass('ai-provider')}>AI Provider</button>
              )}
              {isAdmin && <button onClick={() => setActiveTab('api-keys')} className={tabClass('api-keys')}>API Keys</button>}
            </div>
          </div>

          <div className="flex-1 overflow-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <p className="text-text-secondary">Loading...</p>
              </div>
            ) : (
              <>
                {activeTab === 'organization' && organization && (
                  <OrganizationTab
                    org={organization}
                    userCount={users.length}
                    domainInput={domainInput}
                    domainSaving={domainSaving}
                    domainSaved={domainSaved}
                    domainError={domainError}
                    onDomainChange={setDomainInput}
                    onDomainSave={handleSaveDomains}
                  />
                )}
                {activeTab === 'fields' && fieldConfigs.length > 0 && (
                  <InitiativeFieldsTab
                    fieldConfigs={fieldConfigs}
                    editedFields={editedFields}
                    fieldConfigSaving={fieldConfigSaving}
                    hasFieldChanges={Object.keys(editedFields).length > 0}
                    onFieldUpdate={(fieldKey, prop, value) =>
                      setEditedFields(prev => ({ ...prev, [fieldKey]: { ...prev[fieldKey], [prop]: value } }))
                    }
                    onSave={handleSaveFieldConfigs}
                    onReset={() => setEditedFields({})}
                  />
                )}
                {activeTab === 'labs' && (
                  <LabsTab
                    labsFeatures={labsFeatures}
                    labsSaving={labsSaving}
                    labsSaved={labsSaved}
                    labsSaveError={labsSaveError}
                    onToggle={handleToggleLabsFeature}
                    onSave={handleSaveLabsFeatures}
                  />
                )}
                {activeTab === 'ai-provider' && <AiProviderTab />}
                {activeTab === 'hierarchy' && (
                  <HierarchyTab
                    labelDraft={labelDraft}
                    labelSaving={labelSaving}
                    labelSaved={labelSaved}
                    labelSaveError={labelSaveError}
                    onLabelChange={(key, value) => setLabelDraft((d) => ({ ...d, [key]: value }))}
                    onSaveLabels={handleSaveLabels}
                    products={products}
                    allSubProducts={subProducts}
                    onAddProduct={() => { setSelectedProduct(null); setProductModalOpen(true); }}
                    onEditProduct={(p) => { setSelectedProduct(p); setProductModalOpen(true); }}
                    onAddSubProduct={() => { setSelectedSubProduct(null); setSubProductModalOpen(true); }}
                    onEditSubProduct={(sp) => { setSelectedSubProduct(sp); setSubProductModalOpen(true); }}
                  />
                )}
                {activeTab === 'classifications' && (
                  <ClassificationsTab
                    classifications={classifications}
                    onAdd={() => { setSelectedClassification(null); setClassificationModalOpen(true); }}
                    onEdit={(c) => { setSelectedClassification(c); setClassificationModalOpen(true); }}
                  />
                )}
                {activeTab === 'statuses' && (
                  <StatusTab
                    statuses={statuses}
                    onAdd={() => { setSelectedStatus(null); setStatusModalOpen(true); }}
                    onEdit={(s) => { setSelectedStatus(s); setStatusModalOpen(true); }}
                  />
                )}
                {activeTab === 'tags' && (
                  <TagsTab
                    tags={tags}
                    onAdd={() => { setSelectedTag(null); setTagModalOpen(true); }}
                    onEdit={(t) => { setSelectedTag(t); setTagModalOpen(true); }}
                  />
                )}
                {activeTab === 'users' && (
                  <UsersTab
                    users={users}
                    maxUsers={organization?.maxUsers ?? 100}
                    resendState={resendState}
                    onAdd={() => { setSelectedUser(null); setUserModalOpen(true); }}
                    onEdit={(u) => { setSelectedUser(u); setUserModalOpen(true); }}
                    onResend={handleResendInvite}
                    onRefresh={loadData}
                  />
                )}
                {activeTab === 'timeframes' && (
                  <TimeframesTab
                    fiscalYears={fiscalYears}
                    onAdd={() => { setSelectedFiscalYear(null); setFiscalYearModalOpen(true); }}
                    onEdit={(fy) => { setSelectedFiscalYear(fy); setFiscalYearModalOpen(true); }}
                  />
                )}
                {activeTab === 'api-keys' && isAdmin && (
                  <ApiKeysTab
                    apiKeys={apiKeys}
                    onGenerate={() => setGenerateKeyModalOpen(true)}
                    onRevoke={handleRevokeKey}
                    isRevoking={isRevoking}
                  />
                )}
                {activeTab === 'teams' && isAdmin && <TeamsTab />}
              </>
            )}
          </div>
        </div>
      </main>

      <InstanceFooter />

      {/* Modals */}
      <ProductModal isOpen={productModalOpen} onClose={() => setProductModalOpen(false)} product={selectedProduct} onSuccess={loadData} />
      <SubProductModal isOpen={subProductModalOpen} onClose={() => setSubProductModalOpen(false)} subProduct={selectedSubProduct} onSuccess={loadData} />
      <ClassificationModal isOpen={classificationModalOpen} onClose={() => setClassificationModalOpen(false)} classification={selectedClassification} onSuccess={loadData} />
      <StatusModal isOpen={statusModalOpen} onClose={() => setStatusModalOpen(false)} status={selectedStatus} onSuccess={loadData} />
      <TagModal isOpen={tagModalOpen} onClose={() => setTagModalOpen(false)} tag={selectedTag} onSuccess={loadData} />
      <UserModal isOpen={userModalOpen} onClose={() => setUserModalOpen(false)} user={selectedUser} onSuccess={loadData} allowedEmailDomains={organization?.allowedEmailDomains} isLastAdmin={selectedUser?.role === 'ADMIN' && users.filter(u => u.role === 'ADMIN' && u.isActive).length <= 1} />
      <TimeframeModal isOpen={fiscalYearModalOpen} onClose={() => setFiscalYearModalOpen(false)} fiscalYear={selectedFiscalYear} onSuccess={loadData} />
      <GenerateKeyModal
        isOpen={generateKeyModalOpen}
        onClose={() => setGenerateKeyModalOpen(false)}
        onSuccess={(newKey) => setApiKeys((prev) => [newKey, ...prev])}
      />
    </div>
  );
}
